<template>
  <div id="parceria-view">
    <div id="breadcrumb">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/">Início</router-link>
          </li>

          <li class="breadcrumb-item">
            <router-link to="/parcerias">Parcerias</router-link>
          </li>

          <li class="breadcrumb-item active" aria-current="page">
            {{ modoEdicao ? "Editar Parceria Externa" : "Parceria Externa" }}
          </li>
        </ol>
      </nav>
    </div>

    <div class="formulario-container">
      <div class="cabecalho">
        <div>
          <span class="tag">
            {{
              modoEdicao
                ? "EDIÇÃO DE SOLICITAÇÃO DE PARCERIA"
                : "FORMULÁRIO DE LEVANTAMENTO DE DEMANDA"
            }}
          </span>

          <h1>
            {{
              modoEdicao
                ? "Editar Parceria Externa para Ações de Extensão"
                : "Parceria Externa para Ações de Extensão"
            }}
          </h1>

          <p>
            Este formulário deve ser preenchido por extensionistas
            coordenadores(as) de ações de extensão da UFC que desejam
            estabelecer, com apoio da Coordenadoria de Articulação Social
            (PREX/Articula), uma parceria com uma entidade externa à
            universidade — como órgãos públicos, escolas, ONGs/OSCs ou
            empresas.<br />
            As informações coletadas orientarão a prospecção, a articulação e,
            quando necessário, a formalização da parceria.
          </p>

          <span class="tempo">
            <i class="fa-regular fa-clock"></i>
            Tempo estimado de preenchimento: 5 a 7 minutos.
          </span>
        </div>

        <img src="@/assets/brasaoUFC.png" alt="Logo PREX" class="logo" />
      </div>

      <div v-if="carregandoAcao" class="aviso-carregando">
        <i class="fa-solid fa-spinner fa-spin"></i>

        <div>
          <strong>
            {{
              modoEdicao
                ? "Carregando dados da solicitação..."
                : "Carregando dados da ação..."
            }}
          </strong>

          <p>
            {{
              modoEdicao
                ? "Estamos carregando as informações preenchidas na solicitação de parceria."
                : "Estamos preenchendo previamente o formulário com as informações da ação de extensão."
            }}
          </p>
        </div>
      </div>

      <div v-if="acaoCarregada && !carregandoAcao && avisoAcaoVisivel" class="aviso-acao">
        <i class="fa-solid fa-circle-info"></i>

        <div>
          <strong>
            {{
              modoEdicao
                ? "Modo de edição da solicitação"
                : "Dados da ação carregados"
            }}
          </strong>

          <p>
            {{
              modoEdicao
                ? "Ao salvar em edição, a solicitação fica com status 'Em edição' (oculta para a PREX até que seja reenviada). Para submetê-la novamente à PREX após as alterações, utilize o botão 'Enviar' na listagem de solicitações da ação."
                : "Algumas informações foram preenchidas automaticamente com base nos dados cadastrados na ação. Você pode editar qualquer campo antes de enviar o formulário."
            }}
          </p>
        </div>

        <button type="button" @click="fecharAvisoAcao">X</button>
      </div>
      <div class="progresso-container">
        <div class="progresso-topo">
          <span> Etapa {{ etapaAtual }} de {{ totalEtapas }} </span>

          <span> {{ progresso }}% </span>
        </div>

        <div class="barra-progresso">
          <div class="barra-progresso-preenchida" :style="{ width: `${progresso}%` }"></div>
        </div>

        <div class="etapas">
          <div v-for="etapa in totalEtapas" :key="etapa" class="etapa" :class="{
            ativa: etapa === etapaAtual,
            concluida: etapa < etapaAtual,
          }">
            <span>{{ etapa }}</span>

            <small>
              {{ nomesEtapas[etapa - 1] }}
            </small>
          </div>
        </div>
      </div>

      <!-- FORMULÁRIO -->
      <form class="formulario" @submit.prevent="avancar" novalidate>
        <!-- ================================================= -->
        <!-- ETAPA 1 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 1">
          <div class="secao-titulo">
            <span>SEÇÃO 1</span>

            <h2>Identificação do(a) coordenador(a) e da ação</h2>

            <p>
              Informe os dados do coordenador e da ação de extensão vinculada à
              solicitação.
            </p>
          </div>

          <!-- 1 -->
          <div class="campo">
            <label for="nome"> 1. Nome completo <b>*</b> </label>

            <input id="nome" v-model="form.nome" type="text" maxlength="150" autocomplete="name"
              placeholder="Digite seu nome completo" :class="{ erro: erros.nome }" @input="limparErro('nome')" />

            <span v-if="erros.nome" class="mensagem-erro">
              {{ erros.nome }}
            </span>
          </div>

          <!-- 2 -->
          <div class="campo">
            <label for="siape"> 2. SIAPE / matrícula <b>*</b> </label>

            <input id="siape" v-model="form.siape" type="text" maxlength="30" inputmode="numeric"
              placeholder="Digite seu SIAPE ou matrícula" :class="{ erro: erros.siape }" @input="normalizarSiape" />

            <span v-if="erros.siape" class="mensagem-erro">
              {{ erros.siape }}
            </span>
          </div>

          <!-- 3 -->
          <div class="campo">
            <label for="email"> 3. E-mail institucional <b>*</b> </label>

            <input id="email" v-model="form.email" type="email" maxlength="150" autocomplete="email"
              placeholder="exemplo@ufc.br" :class="{ erro: erros.email }" @input="limparErro('email')" />

            <span v-if="erros.email" class="mensagem-erro">
              {{ erros.email }}
            </span>
          </div>

          <!-- 4 -->
          <div class="campo">
            <label for="telefone">
              4. Telefone / WhatsApp para contato <b>*</b>
            </label>

            <input id="telefone" v-model="form.telefone" type="tel" maxlength="20" inputmode="tel"
              placeholder="(88) 99999-9999" :class="{ erro: erros.telefone }" @input="aoDigitarTelefone" />

            <span v-if="erros.telefone" class="mensagem-erro">
              {{ erros.telefone }}
            </span>
          </div>

          <!-- 5 -->
          <div class="campo">
            <label for="unidadeCurso">
              5. Unidade acadêmica de vinculação e Curso <b>*</b>
            </label>

            <input id="unidadeCurso" v-model="form.unidadeCurso" type="text" maxlength="200"
              placeholder="Ex.: Campus de Russas — Ciência da Computação" :class="{ erro: erros.unidadeCurso }"
              @input="limparErro('unidadeCurso')" />

            <span v-if="erros.unidadeCurso" class="mensagem-erro">
              {{ erros.unidadeCurso }}
            </span>
          </div>

          <!-- 6 -->
          <div class="campo">
            <label for="campus"> 6. Campus de atuação <b>*</b> </label>

            <select id="campus" v-model="form.campus" :class="{ erro: erros.campus }" @change="limparErro('campus')">
              <option value="">Selecione o campus</option>

              <option v-for="campus in opcoes.campi" :key="campus" :value="campus">
                {{ campus }}
              </option>
            </select>

            <span v-if="erros.campus" class="mensagem-erro">
              {{ erros.campus }}
            </span>
          </div>

          <!-- 7 -->
          <div class="campo">
            <label for="sigaa">
              7. Número da ação de extensão no SIGAA <b>*</b>
            </label>

            <input id="sigaa" v-model="form.sigaa" type="text" maxlength="30" inputmode="numeric"
              placeholder="Número da ação no SIGAA" :class="{ erro: erros.sigaa }" @input="normalizarSigaa" />

            <span v-if="erros.sigaa" class="mensagem-erro">
              {{ erros.sigaa }}
            </span>
          </div>

          <!-- 8 -->
          <div class="campo">
            <label for="nomeAcao"> 8. Nome da ação de extensão <b>*</b> </label>

            <input id="nomeAcao" v-model="form.nomeAcao" type="text" maxlength="250"
              placeholder="Nome da ação de extensão" :class="{ erro: erros.nomeAcao }"
              @input="limparErro('nomeAcao')" />

            <span v-if="erros.nomeAcao" class="mensagem-erro">
              {{ erros.nomeAcao }}
            </span>
          </div>

          <!-- 9 -->
          <div class="campo">
            <label> 9. Qual é o tipo de ação de extensão? <b>*</b> </label>

            <p class="ajuda">
              Pode selecionar mais de uma opção, se necessário.
            </p>

            <div class="checkbox-list">
              <label v-for="tipo in opcoes.tiposAcao" :key="tipo" class="checkbox-item">
                <input v-model="form.tiposAcao" type="checkbox" :value="tipo" @change="limparErro('tiposAcao')" />

                <span>{{ tipo }}</span>
              </label>
            </div>

            <span v-if="erros.tiposAcao" class="mensagem-erro">
              {{ erros.tiposAcao }}
            </span>
          </div>

          <!-- 10 -->
          <div class="campo">
            <label>
              10. Qual é a área temática principal da ação? <b>*</b>
            </label>

            <p class="ajuda">
              Conforme as áreas definidas na Resolução nº 05/CEPE, de 07 de
              março de 2025.
            </p>

            <div class="radio-list">
              <label v-for="area in opcoes.areasTematicas" :key="area" class="radio-item">
                <input v-model="form.areaTematica" type="radio" name="areaTematica" :value="area"
                  @change="limparErro('areaTematica')" />

                <span>{{ area }}</span>
              </label>
            </div>

            <span v-if="erros.areaTematica" class="mensagem-erro">
              {{ erros.areaTematica }}
            </span>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 2 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 2">
          <div class="secao-titulo">
            <span>SEÇÃO 2</span>

            <h2>Sobre a parceria externa pretendida</h2>

            <p>
              Conte-nos sobre a entidade parceira ou sobre o tipo de parceiro
              que você está procurando.
            </p>
          </div>

          <!-- 11 -->
          <div class="campo">
            <label>
              11. Já existe uma entidade específica em vista para esta parceria?
              <b>*</b>
            </label>

            <div class="radio-list">
              <label v-for="opcao in opcoes.entidadeVista" :key="opcao" class="radio-item">
                <input v-model="form.entidadeVista" type="radio" name="entidadeVista" :value="opcao"
                  @change="limparErro('entidadeVista')" />

                <span>{{ opcao }}</span>
              </label>
            </div>

            <span v-if="erros.entidadeVista" class="mensagem-erro">
              {{ erros.entidadeVista }}
            </span>
          </div>

          <!-- 12 -->
          <div class="campo">
            <label for="contatoEntidade">
              12. Caso já exista uma entidade em vista, informe o nome
            </label>

            <input id="contatoEntidade" v-model="form.contatoEntidade" type="text" maxlength="1000"
              placeholder="Nome da entidade" />
          </div>


          <!-- 13 -->
          <div class="campo">
            <label>
              13. Caso ainda não tenha uma entidade definida, que tipo de
              parceiro você procura?
            </label>

            <p class="ajuda">Pode selecionar mais de uma opção.</p>

            <div class="checkbox-list">
              <label v-for="tipo in opcoes.tiposParceiro" :key="tipo" class="checkbox-item">
                <input v-model="form.tiposParceiro" type="checkbox" :value="tipo" />

                <span>{{ tipo }}</span>
              </label>
            </div>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 3 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 3">
          <div class="secao-titulo">
            <span>SEÇÃO 3</span>

            <h2>Detalhamento da proposta</h2>

            <p>
              Explique o que está sendo proposto e o público que será
              beneficiado.
            </p>
          </div>

          <!-- 14 -->
          <div class="campo">
            <label for="proposta">
              14. Descreva a proposta de parceria que você deseja apresentar
              <b>*</b>
            </label>

            <p class="ajuda">
              Explique de forma clara o que está sendo proposto e qual o papel
              esperado da entidade parceira.
            </p>

            <textarea id="proposta" v-model="form.proposta" rows="7" maxlength="3000"
              placeholder="Descreva a proposta de parceria..." :class="{ erro: erros.proposta }"
              @input="limparErro('proposta')"></textarea>

            <div class="contador">{{ form.proposta.length }}/3000</div>

            <span v-if="erros.proposta" class="mensagem-erro">
              {{ erros.proposta }}
            </span>
          </div>

          <!-- 15 -->
          <div class="campo">
            <label for="beneficiarios">
              15. Quem são os beneficiários diretos e qual o número estimado?
              <b>*</b>
            </label>

            <textarea id="beneficiarios" v-model="form.beneficiarios" rows="5" maxlength="1500"
              placeholder="Descreva quem será beneficiado..." :class="{ erro: erros.beneficiarios }"
              @input="limparErro('beneficiarios')"></textarea>

            <div class="contador">{{ form.beneficiarios.length }}/1500</div>

            <span v-if="erros.beneficiarios" class="mensagem-erro">
              {{ erros.beneficiarios }}
            </span>

            <div class="radio-list">
              <label v-for="opcao in opcoes.quantidadeBeneficiarios" :key="opcao" class="radio-item">
                <input v-model="form.quantidadeBeneficiarios" type="radio" name="quantidadeBeneficiarios" :value="opcao"
                  @change="limparErro('quantidadeBeneficiarios')" />

                <span>{{ opcao }}</span>
              </label>
            </div>

            <span v-if="erros.quantidadeBeneficiarios" class="mensagem-erro">
              {{ erros.quantidadeBeneficiarios }}
            </span>
          </div>

          <!-- 17 -->
          <div class="campo">
            <label>
              16. Qual é a abrangência territorial prevista para as ações?
              <b>*</b>
            </label>

            <div class="radio-list">
              <label v-for="opcao in opcoes.abrangencia" :key="opcao" class="radio-item">
                <input v-model="form.abrangencia" type="radio" name="abrangencia" :value="opcao"
                  @change="limparErro('abrangencia')" />

                <span>{{ opcao }}</span>
              </label>
            </div>

            <span v-if="erros.abrangencia" class="mensagem-erro">
              {{ erros.abrangencia }}
            </span>
          </div>

          <!-- 18 -->
          <div class="campo">
            <label>
              17. A proposta beneficia diretamente grupos em situação de
              vulnerabilidade ou historicamente excluídos?
            </label>

            <p class="ajuda">Selecione todas as que se aplicam.</p>

            <div class="checkbox-list">
              <label v-for="grupo in opcoes.gruposVulnerabilidade" :key="grupo" class="checkbox-item">
                <input v-model="form.gruposVulnerabilidade" type="checkbox" :value="grupo"
                  @change="tratarNenhumGrupo" />

                <span>{{ grupo }}</span>
              </label>
            </div>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- ETAPA 4 -->
        <!-- ================================================= -->

        <section v-if="etapaAtual === 4">
          <div class="secao-titulo">
            <span>SEÇÃO 4</span>

            <h2>Contrapartidas e encerramento</h2>

            <p>
              Informe as possíveis contrapartidas e revise suas informações
              antes do envio.
            </p>
          </div>

          <!-- 19 -->
          <div class="campo">
            <label for="contrapartidasOferecidas">
              18. Que contrapartidas sua ação pode oferecer à entidade parceira?
            </label>

            <p class="ajuda">
              Ex.: know-how técnico, formação/capacitação, certificação,
              divulgação institucional, mão de obra estudantil etc.
            </p>

            <textarea id="contrapartidasOferecidas" v-model="form.contrapartidasOferecidas" rows="6" maxlength="2500"
              placeholder="Descreva as contrapartidas..."></textarea>

            <div class="contador">
              {{ form.contrapartidasOferecidas.length }}/2500
            </div>
          </div>

          <!-- 20 -->
          <div class="campo">
            <label>
              19. Que contrapartidas você espera ou já foram sinalizadas pela
              entidade parceira?
            </label>

            <p class="ajuda">Selecione todas as que se aplicam.</p>

            <div class="checkbox-list">
              <label v-for="opcao in opcoes.contrapartidasEsperadas" :key="opcao" class="checkbox-item">
                <input v-model="form.contrapartidasEsperadas" type="checkbox" :value="opcao"
                  @change="tratarNenhumaContrapartida" />

                <span>{{ opcao }}</span>
              </label>
            </div>
          </div>

          <!-- 21 -->
          <div class="campo">
            <label>
              20. Qual é a duração prevista das ações desta parceria? <b>*</b>
            </label>

            <div class="radio-list">
              <label v-for="opcao in opcoes.duracao" :key="opcao" class="radio-item">
                <input v-model="form.duracao" type="radio" name="duracao" :value="opcao"
                  @change="limparErro('duracao')" />

                <span>{{ opcao }}</span>
              </label>
            </div>

            <span v-if="erros.duracao" class="mensagem-erro">
              {{ erros.duracao }}
            </span>
          </div>

          <!-- 22 -->
          <div class="campo">
            <label for="observacoes">
              21. Observações adicionais ou informações relevantes para a
              análise da PREX
            </label>

            <textarea id="observacoes" v-model="form.observacoes" rows="6" maxlength="2500"
              placeholder="Caso necessário, informe outras informações relevantes..."></textarea>

            <div class="contador">{{ form.observacoes.length }}/2500</div>
          </div>

          <!-- DECLARAÇÃO -->
          <div class="declaracao">
            <label class="checkbox-declaracao">
              <input v-model="form.declaracao" type="checkbox" @change="limparErro('declaracao')" />

              <span>
                Declaro que as informações prestadas são verídicas e estou
                ciente de que o registro constitui etapa de triagem preliminar,
                não configurando compromisso formal por parte da UFC. Também
                estou ciente de que os dados fornecidos serão utilizados
                exclusivamente para análise e articulação da parceria pela
                equipe da PREX/Articula.
                <b>*</b>
              </span>
            </label>

            <span v-if="erros.declaracao" class="mensagem-erro">
              {{ erros.declaracao }}
            </span>
          </div>
        </section>

        <!-- NAVEGAÇÃO -->
        <div class="acoes-formulario">
          <button v-if="etapaAtual > 1" type="button" class="btn-voltar" :disabled="enviandoFormulario" @click="voltar">
            <i class="fa-solid fa-arrow-left"></i>
            Voltar
          </button>

          <button v-if="etapaAtual < totalEtapas" type="submit" class="btn-continuar">
            Continuar
            <i class="fa-solid fa-arrow-right"></i>
          </button>

          <template v-else>
            <button
              v-if="modoEdicao"
              type="button"
              class="btn-guardar"
              :disabled="enviandoFormulario"
              @click="salvarEmEdicao"
            >
              <i v-if="enviandoFormulario" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-floppy-disk"></i>
              Salvar em edição
            </button>

            <button
              v-else
              type="button"
              class="btn-enviar"
              :disabled="enviandoFormulario"
              @click="enviarFormulario"
            >
              <i v-if="enviandoFormulario" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-paper-plane"></i>
              {{ enviandoFormulario ? "Enviando..." : "Enviar formulário" }}
            </button>
          </template>
        </div>
      </form>
    </div>
    <Aviso v-if="mostrarModal" :sucesso="modalSucesso" :texto="mensagemModal" @fecharModal="finalizarModal" />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import acoesService from "@/service/acaoService";
import parceriaService from "@/service/parceriaService";
import usuarioService from "@/service/usuariosService";
import Aviso from "@/components/common/aviso.vue";

const route = useRoute();
const router = useRouter();
const enviandoFormulario = ref(false);

const parceriaId = computed(() => route.query.parceriaId || null);
const modoEdicao = computed(() => Boolean(parceriaId.value));
const acaoExtensaoIdCarregada = ref("");

const mostrarModal = ref(false);
const modalSucesso = ref(false);
const mensagemModal = ref("");

const etapaAtual = ref(1);
const totalEtapas = 4;

const carregandoAcao = ref(false);
const acaoCarregada = ref(false);

const avisoAcaoVisivel = ref(true);

const nomesEtapas = ["Identificação", "Parceria", "Proposta", "Contrapartidas"];

const form = reactive({
  nome: "",
  siape: "",
  email: "",
  telefone: "",
  unidadeCurso: "",
  campus: "",
  sigaa: "",
  nomeAcao: "",
  tiposAcao: [],
  areaTematica: "",

  entidadeVista: "",
  contatoEntidade: "",
  tiposParceiro: [],

  proposta: "",
  beneficiarios: "",
  quantidadeBeneficiarios: "",
  abrangencia: "",
  gruposVulnerabilidade: [],

  contrapartidasOferecidas: "",
  contrapartidasEsperadas: [],
  duracao: "",
  observacoes: "",

  declaracao: false,
});

const erros = reactive({});

const opcoes = {
  campi: [
    "Campus do Benfica (Fortaleza)",
    "Campus do Pici (Fortaleza)",
    "Campus do Porangabuçu (Fortaleza)",
    "Campus de Sobral",
    "Campus de Quixadá",
    "Campus de Russas",
    "Campus de Crateús",
    "Campus de Itapajé",
  ],

  tiposAcao: [
    "Projeto de extensão",
    "Programa de extensão",
    "Curso de extensão",
    "Evento de extensão",
    "ACCS — Ações Curriculares de Extensão",
    "Prestação de serviço especializado — serviços técnicos, laboratoriais, de consultoria, etc.",
  ],

  areasTematicas: [
    "Comunicação",
    "Cultura",
    "Direitos Humanos e Justiça",
    "Educação",
    "Meio Ambiente",
    "Saúde",
    "Tecnologia e Produção",
    "Trabalho",
  ],

  entidadeVista: [
    "Sim, já tenho contato estabelecido com a entidade",
    "Não, gostaria de indicação/apoio da PREX na prospecção",
    "Estou em negociação preliminar",
  ],

  tiposParceiro: [
    "Órgão ou entidade da administração pública federal",
    "Órgão ou entidade da administração pública estadual",
    "Órgão ou entidade da administração pública municipal",
    "Escola pública de educação básica",
    "Organização da Sociedade Civil (OSC/ONG/Associação/Cooperativa)",
    "Empresa privada / Pessoa jurídica de direito privado",
    "Ainda não sei / Gostaria de orientação da PREX",
  ],

  quantidadeBeneficiarios: [
    "Menos de 50 pessoas",
    "De 50 a 200 pessoas",
    "De 201 a 500 pessoas",
    "De 501 a 1.000 pessoas",
    "Mais de 1.000 pessoas",
    "Não é possível estimar",
  ],

  abrangencia: [
    "Local (bairro ou comunidade específica)",
    "Municipal (um município)",
    "Regional (mais de um município ou região)",
    "Estadual",
    "Nacional ou internacional",
  ],

  gruposVulnerabilidade: [
    "Pessoas em situação de pobreza ou vulnerabilidade socioeconômica",
    "Pessoas negras, quilombolas ou indígenas",
    "Mulheres em situação de vulnerabilidade",
    "Pessoas com deficiência",
    "Pessoas LGBTQIA+",
    "Povos e comunidades tradicionais (ribeirinhos, extrativistas, etc.)",
    "Populações rurais ou do semiárido",
    "Nenhum dos grupos acima",
  ],

  contrapartidasEsperadas: [
    "Contrapartida financeira",
    "Espaço físico para realização das atividades",
    "Equipamentos, materiais ou insumos",
    "Acesso a dados, informações ou documentação",
    "Transporte ou alimentação para participantes",
    "Mão de obra ou pessoal técnico especializado",
    "Bolsas ou auxílios para estudantes",
    "Nenhuma / ainda não definido",
  ],

  duracao: [
    "Até 3 meses",
    "De 3 a 6 meses",
    "De 6 meses a 1 ano",
    "Mais de 1 ano",
    "Sem prazo definido / Contínua",
  ],
};

const progresso = computed(() => {
  return Math.round((etapaAtual.value / totalEtapas) * 100);
});

function fecharAvisoAcao() {
  avisoAcaoVisivel.value = false;
}
function abrirModalSucesso(mensagem) {
  modalSucesso.value = true;
  mensagemModal.value = mensagem;
  mostrarModal.value = true;
}

function abrirModalErro(mensagem) {
  modalSucesso.value = false;
  mensagemModal.value = mensagem;
  mostrarModal.value = true;
}

function obterAcaoIdDestino() {
  return route.params.id || route.params.acaoId || acaoExtensaoIdCarregada.value || "";
}

function finalizarModal() {
  if (modalSucesso.value || (modoEdicao.value && !acaoCarregada.value)) {
    const acaoId = obterAcaoIdDestino();

    mostrarModal.value = false;

    if (acaoId) {
      router.push(`/acao/${acaoId}/parcerias`);
    } else {
      router.push("/parcerias");
    }

    return;
  }

  mostrarModal.value = false;
}

async function carregarParceriaParaEdicao() {
  if (!parceriaId.value) return;

  carregandoAcao.value = true;

  try {
    const dados = await parceriaService.buscarParceriaPorId(parceriaId.value);
    if (!dados) return;

    const statusPermitidos = ["EM_EDICAO", "FORMULARIO_RECEBIDO", "CANCELADA", "REJEITADA"];
    if (dados.status && !statusPermitidos.includes(dados.status)) {
      abrirModalErro("Esta solicitação não pode ser editada pois já se encontra em tramitação ativa pela PREX.");
      return;
    }

    if (dados.acaoExtensaoId) {
      acaoExtensaoIdCarregada.value = dados.acaoExtensaoId;
    }

    form.nome = dados.nome ?? "";
    form.siape = dados.siape ?? "";
    form.email = dados.email ?? "";
    form.telefone = formatarTelefone(dados.telefone ?? "");
    form.unidadeCurso = dados.unidadeCurso ?? "";
    form.campus = dados.campus ?? "";
    form.sigaa = dados.sigaa ?? "";
    form.nomeAcao = limparAspasExcedentes(dados.nomeAcao ?? dados.tituloAcao ?? "");
    form.tiposAcao = Array.isArray(dados.tiposAcao) ? [...dados.tiposAcao] : [];
    form.areaTematica = dados.areaTematica ?? "";

    form.entidadeVista = dados.entidadeVista ?? "";
    form.contatoEntidade = dados.contatoEntidade ?? "";
    form.tiposParceiro = Array.isArray(dados.tiposParceiro) ? [...dados.tiposParceiro] : [];

    form.proposta = dados.proposta ?? "";
    form.beneficiarios = dados.beneficiarios ?? "";
    form.quantidadeBeneficiarios = dados.quantidadeBeneficiarios ?? "";
    form.abrangencia = dados.abrangencia ?? "";
    form.gruposVulnerabilidade = Array.isArray(dados.gruposVulnerabilidade)
      ? [...dados.gruposVulnerabilidade]
      : [];

    form.contrapartidasOferecidas = dados.contrapartidasOferecidas ?? "";
    form.contrapartidasEsperadas = Array.isArray(dados.contrapartidasEsperadas)
      ? [...dados.contrapartidasEsperadas]
      : [];
    form.duracao = dados.duracao ?? "";
    form.observacoes = dados.observacoes ?? "";
    form.declaracao = Boolean(dados.declaracao);

    acaoCarregada.value = true;
  } catch (error) {
    console.error("Erro ao carregar parceria para edição:", error);
    abrirModalErro("Não foi possível carregar os dados da solicitação para edição.");
  } finally {
    carregandoAcao.value = false;
  }
}

async function carregarDadosAcao() {
  const acaoId = route.params.id;

  if (!acaoId) {
    console.warn("Nenhum ID de ação foi encontrado na rota.");
    return;
  }

  carregandoAcao.value = true;

  try {
    const acao = await acoesService.getAcoesById(acaoId);

    if (!acao) {
      console.warn("A API não retornou os dados da ação.");
      return;
    }

    preencherFormularioComAcao(acao);

    acaoCarregada.value = true;
  } catch (error) {
    console.error("Erro ao carregar dados da ação:", error);
  } finally {
    carregandoAcao.value = false;
  }
}

async function carregarDadosUsuarioLogado() {
  try {
    const usuario = await usuarioService.getUsuarioLogado();

    if (!usuario) {
      console.warn("A API não retornou os dados do usuário logado.");
      return;
    }

    if (textoValido(usuario.email)) {
      form.email = usuario.email.trim();
    }

    if (textoValido(usuario.telefone)) {
      form.telefone = formatarTelefone(usuario.telefone);
    }
  } catch (error) {
    console.error("Erro ao carregar usuário logado:", error);
  }
}

function preencherFormularioComAcao(acao) {
  if (textoValido(acao.nomeCoordenador)) {
    form.nome = limparAspasExcedentes(acao.nomeCoordenador);
  }
  if (textoValido(acao.titulo)) {
    form.nomeAcao = limparAspasExcedentes(acao.titulo);
  }
}

function limparAspasExcedentes(texto) {
  if (typeof texto !== "string") {
    return "";
  }

  return texto
    .trim()
    .replace(/^"+|"+$/g, "")
    .trim();
}

function normalizarSiape() {
  form.siape = form.siape.replace(/\D/g, "").slice(0, 30);

  limparErro("siape");
}

function normalizarSigaa() {
  form.sigaa = form.sigaa.replace(/\D/g, "").slice(0, 30);

  limparErro("sigaa");
}

function formatarTelefone(valor) {
  if (valor === null || valor === undefined) {
    return "";
  }

  let numeros = String(valor).replace(/\D/g, "");

  // Remove o código do Brasil quando vier como +55 ou 55.
  if (
    numeros.startsWith("55") &&
    (numeros.length === 12 || numeros.length === 13)
  ) {
    numeros = numeros.slice(2);
  }

  // Celular brasileiro: (88) 94002-7778
  if (numeros.length === 11) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  }

  // Telefone fixo: (88) 4002-7778
  if (numeros.length === 10) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  }

  // Durante a digitação
  if (numeros.length === 9) {
    return `${numeros.slice(0, 5)}-${numeros.slice(5)}`;
  }

  if (numeros.length === 8) {
    return `${numeros.slice(0, 4)}-${numeros.slice(4)}`;
  }

  return numeros;
}

function aoDigitarTelefone(event) {
  const valor = event?.target?.value ?? "";

  form.telefone = formatarTelefone(valor);

  limparErro("telefone");
}

function limparErros() {
  Object.keys(erros).forEach((key) => {
    delete erros[key];
  });
}

function limparErro(campo) {
  delete erros[campo];
}

function textoValido(valor) {
  return typeof valor === "string" && valor.trim().length > 0;
}

function emailValido(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function telefoneValido(telefone) {
  if (!textoValido(telefone)) {
    return false;
  }

  return /^\(\d{2}\) \d{4,5}-\d{4}$/.test(telefone.trim());
}

function validarEtapa1() {
  limparErros();

  if (!textoValido(form.nome)) {
    erros.nome = "Informe o nome completo.";
  }

  if (!textoValido(form.siape)) {
    erros.siape = "Informe o SIAPE ou matrícula.";
  }

  if (!textoValido(form.email)) {
    erros.email = "Informe o e-mail institucional.";
  } else if (!emailValido(form.email)) {
    erros.email = "Informe um e-mail válido.";
  }

  if (!textoValido(form.telefone)) {
    erros.telefone = "Informe um telefone para contato.";
  } else if (!telefoneValido(form.telefone)) {
    erros.telefone = "Informe um telefone válido.";
  }

  if (!textoValido(form.unidadeCurso)) {
    erros.unidadeCurso = "Informe a unidade acadêmica e o curso.";
  }

  if (!form.campus) {
    erros.campus = "Selecione o campus de atuação.";
  }

  if (!textoValido(form.sigaa)) {
    erros.sigaa = "Informe o número da ação no SIGAA.";
  }

  if (!textoValido(form.nomeAcao)) {
    erros.nomeAcao = "Informe o nome da ação de extensão.";
  }

  if (!form.tiposAcao.length) {
    erros.tiposAcao = "Selecione pelo menos um tipo de ação.";
  }

  if (!form.areaTematica) {
    erros.areaTematica = "Selecione a área temática principal.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa2() {
  limparErros();

  if (!form.entidadeVista) {
    erros.entidadeVista = "Selecione uma opção.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa3() {
  limparErros();

  if (!textoValido(form.proposta)) {
    erros.proposta = "Descreva a proposta de parceria.";
  } else if (form.proposta.trim().length < 30) {
    erros.proposta =
      "A descrição da proposta deve ter pelo menos 30 caracteres.";
  }

  if (!textoValido(form.beneficiarios)) {
    erros.beneficiarios = "Informe quem são os beneficiários diretos.";
  }

  if (!form.quantidadeBeneficiarios) {
    erros.quantidadeBeneficiarios =
      "Selecione a quantidade estimada de beneficiários.";
  }

  if (!form.abrangencia) {
    erros.abrangencia = "Selecione a abrangência territorial.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapa4() {
  limparErros();

  if (!form.duracao) {
    erros.duracao = "Selecione a duração prevista.";
  }

  if (!form.declaracao) {
    erros.declaracao = "Você precisa aceitar a declaração para continuar.";
  }

  return Object.keys(erros).length === 0;
}

function validarEtapaAtual() {
  switch (etapaAtual.value) {
    case 1:
      return validarEtapa1();

    case 2:
      return validarEtapa2();

    case 3:
      return validarEtapa3();

    case 4:
      return validarEtapa4();

    default:
      return false;
  }
}

function avancar() {
  if (!validarEtapaAtual()) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    return;
  }

  if (etapaAtual.value < totalEtapas) {
    etapaAtual.value++;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function voltar() {
  if (etapaAtual.value > 1) {
    limparErros();

    etapaAtual.value--;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function tratarNenhumGrupo() {
  const nenhum = "Nenhum dos grupos acima";

  if (
    form.gruposVulnerabilidade.includes(nenhum) &&
    form.gruposVulnerabilidade.length > 1
  ) {
    form.gruposVulnerabilidade = form.gruposVulnerabilidade.filter(
      (item) => item === nenhum
    );
  }
}

function tratarNenhumaContrapartida() {
  const nenhuma = "Nenhuma / ainda não definido";

  if (
    form.contrapartidasEsperadas.includes(nenhuma) &&
    form.contrapartidasEsperadas.length > 1
  ) {
    form.contrapartidasEsperadas = form.contrapartidasEsperadas.filter(
      (item) => item === nenhuma
    );
  }
}

function construirPayload() {
  const payload = {
    ...JSON.parse(JSON.stringify(form)),
    acaoExtensaoId: obterAcaoIdDestino(),
    tipoParceria: "EXTERNA",
  };
  payload.nomeAcao = limparAspasExcedentes(payload.nomeAcao);
  return payload;
}

async function salvarEmEdicao() {
  if (enviandoFormulario.value || !modoEdicao.value) {
    return;
  }

  if (!validarEtapa4()) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    return;
  }

  enviandoFormulario.value = true;
  const payload = construirPayload();

  try {
    await parceriaService.atualizarParceria(parceriaId.value, payload);
    abrirModalSucesso(
      "Alterações salvas! A solicitação está com status 'Em edição' e deve ser reenviada à PREX quando finalizada."
    );
  } catch (error) {
    console.error("Erro ao salvar parceria em edição:", error);
    const mensagem =
      error.response?.data?.message ||
      error.response?.data?.mensagem ||
      "Não foi possível salvar as alterações da solicitação.";
    abrirModalErro(mensagem);
  } finally {
    enviandoFormulario.value = false;
  }
}

async function enviarFormulario() {
  if (enviandoFormulario.value) {
    return;
  }

  if (!validarEtapa4()) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    return;
  }

  enviandoFormulario.value = true;
  const payload = construirPayload();

  try {
    if (modoEdicao.value) {
      await parceriaService.atualizarParceria(parceriaId.value, payload);
      await parceriaService.enviarParceria(parceriaId.value);
      abrirModalSucesso(
        "Solicitação atualizada e enviada para análise da PREX com sucesso!"
      );
    } else {
      const resposta = await parceriaService.criarParceria(payload);
      console.log("SUCESSO:", resposta);
      abrirModalSucesso("Parceria externa cadastrada com sucesso!");
    }
  } catch (error) {
    console.error("Erro ao salvar/enviar parceria:", error);

    let mensagem = modoEdicao.value
      ? "Não foi possível atualizar e enviar a solicitação. Tente novamente."
      : "Não foi possível cadastrar a parceria. Tente novamente.";

    if (error.response?.data?.message) {
      mensagem = error.response.data.message;
    } else if (error.response?.data?.mensagem) {
      mensagem = error.response.data.mensagem;
    }

    abrirModalErro(mensagem);
  } finally {
    enviandoFormulario.value = false;
  }
}

onMounted(async () => {
  if (modoEdicao.value) {
    await carregarParceriaParaEdicao();
  } else {
    await Promise.all([carregarDadosAcao(), carregarDadosUsuarioLogado()]);
  }
});
</script>

<style scoped>
#parceria-view {
  min-height: calc(100vh - 130px);
  padding: 32px 8%;
  background: var(--branco);
}

.formulario-container {
  max-width: 1000px;
  margin: 25px auto 0;
}

/* CABEÇALHO */

.cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--cinza-claro);
}

.cabecalho>div {
  flex: 1;
}

.tag {
  display: inline-block;
  color: var(--roxo-primario);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
}

.cabecalho h1 {
  color: var(--preto);
  font-size: 28px;
  margin: 0 0 15px;
}

.cabecalho p {
  color: var(--cinza-escuro);
  line-height: 1.7;
  margin: 0 0 12px;
}

.tempo {
  display: inline-block;
  color: var(--cinza-medio);
  font-size: 13px;
}

.tempo i {
  margin-right: 5px;
}

.logo {
  width: 180px;
  height: auto;
}

/* AVISO CARREGANDO */
.aviso-carregando {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  margin-top: 25px;
  padding: 15px 18px;

  background: var(--branco);
  border-left: 4px solid var(--roxo-claro);
  border-radius: 6px;

  color: var(--cinza-escuro);
}

.aviso-carregando>i {
  color: var(--roxo-claro);
  font-size: 19px;
  margin-top: 2px;
}

.aviso-carregando div {
  flex: 1;
}

.aviso-carregando strong {
  color: var(--roxo-primario);
  font-size: 14px;
}

.aviso-carregando p {
  color: var(--cinza-escuro);
  font-size: 13px;
  margin: 4px 0 0;
}

/* AVISO DADOS DA AÇÃO */
.aviso-acao {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  margin-top: 25px;
  padding: 15px 18px;

  background: var(--roxo-superficie);
  border-left: 4px solid var(--roxo-primario);
  border-radius: 6px;

  color: var(--cinza-escuro);
}

.aviso-acao>i {
  color: var(--roxo-primario);
  font-size: 19px;
  margin-top: 2px;
}

.aviso-acao div {
  flex: 1;
}

.aviso-acao strong {
  color: var(--roxo-primario);
  font-size: 14px;
}

.aviso-acao p {
  color: var(--cinza-escuro);
  font-size: 13px;
  margin: 4px 0 0;
}

.aviso-acao button {
  border: none;
  background: transparent;
  color: var(--cinza-medio);
  cursor: pointer;
  font-weight: bold;
}

/* PROGRESSO */

.progresso-container {
  margin: 30px 0;
}

.progresso-topo {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  font-size: 13px;
  color: var(--cinza-escuro);
  font-weight: 600;
}

.barra-progresso {
  height: 7px;
  background: var(--cinza-claro);
  border-radius: 10px;
  overflow: hidden;
}

.barra-progresso-preenchida {
  height: 100%;
  background: var(--roxo-claro);
  transition: width 0.3s ease;
}

.etapas {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.etapa {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: var(--cinza-medio);
  text-align: center;
}

.etapa span {
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: var(--cinza-claro);

  font-size: 12px;
  font-weight: 700;
}

.etapa small {
  font-size: 11px;
}

.etapa.ativa {
  color: var(--roxo-primario);
}

.etapa.ativa span,
.etapa.concluida span {
  background: var(--roxo-primario);
  color: var(--branco);
}

/* FORMULÁRIO */

.formulario {
  background: var(--branco);

  border: 1px solid var(--cinza-claro);
  border-radius: 10px;

  padding: 32px;

  box-shadow: 0 3px 12px hsla(0, 0%, 0%, 0.06);
}

/* TÍTULO DA SEÇÃO */

.secao-titulo {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--cinza-claro);
}

.secao-titulo>span {
  color: var(--roxo-claro);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.secao-titulo h2 {
  color: var(--preto);
  font-size: 21px;
  margin: 7px 0 8px;
}

.secao-titulo p {
  color: var(--cinza-medio);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

/* CAMPOS */

.campo {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
}

.campo>label {
  color: var(--cinza-escuro);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 7px;
}

.campo>label b {
  color: var(--vermelho-escuro);
}

.campo input[type="text"],
.campo input[type="email"],
.campo input[type="tel"],
.campo select,
.campo textarea {
  width: 100%;
  box-sizing: border-box;

  border: 1px solid var(--cinza-claro);
  border-radius: 7px;

  padding: 11px 13px;

  color: var(--cinza-escuro);
  background: var(--branco);

  font-size: 14px;
  font-family: inherit;

  outline: none;

  transition: border-color 0.15s ease;
}

.campo input:focus,
.campo select:focus,
.campo textarea:focus {
  border-color: var(--roxo-claro);
}

.campo textarea {
  resize: vertical;
  min-height: 100px;
}

.campo input.erro,
.campo select.erro,
.campo textarea.erro {
  border-color: var(--vermelho-escuro);
}

.ajuda {
  color: var(--cinza-medio);
  font-size: 12px;
  line-height: 1.5;
  margin: -2px 0 10px;
}

.mensagem-erro {
  color: var(--vermelho-escuro);
  font-size: 12px;
  margin-top: 5px;
}

.contador {
  align-self: flex-end;
  color: var(--cinza-medio);
  font-size: 11px;
  margin-top: 4px;
}

/* RADIO E CHECKBOX */

.checkbox-list,
.radio-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-item,
.radio-item {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  padding: 10px 12px;

  border: 1px solid var(--cinza-claro);
  border-radius: 7px;

  cursor: pointer;

  font-size: 13px;
  color: var(--cinza-escuro);

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.checkbox-item:hover,
.radio-item:hover {
  background: var(--roxo-superficie);
  border-color: var(--roxo-superficie);
}

.checkbox-item input,
.radio-item input {
  margin-top: 2px;
  accent-color: var(--roxo-claro);
  cursor: pointer;
}

/* DECLARAÇÃO */

.declaracao {
  margin-top: 30px;
  padding: 18px;

  background: var(--branco);
  border-radius: 8px;

  border: 1px solid var(--cinza-claro);
}

.checkbox-declaracao {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  color: var(--cinza-escuro);
  font-size: 13px;
  line-height: 1.6;

  cursor: pointer;
}

.checkbox-declaracao input {
  margin-top: 4px;
  accent-color: var(--roxo-claro);
  flex-shrink: 0;
}

.checkbox-declaracao b {
  color: var(--vermelho-escuro);
}

/* AÇÕES */

.acoes-formulario {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  margin-top: 35px;
  padding-top: 25px;

  border-top: 1px solid var(--cinza-claro);
}

.acoes-formulario button {
  border: none;
  border-radius: 8px;

  padding: 10px 20px;

  font-size: 14px;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.15s ease,
    transform 0.1s ease;
}

.acoes-formulario button:active {
  transform: scale(0.98);
}

.btn-voltar {
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
}

.btn-voltar:hover {
  background: var(--cinza-claro);
}

.btn-guardar {
  background: var(--cinza-claro);
  color: var(--roxo-primario);
}

.btn-guardar:hover {
  background: var(--roxo-superficie);
}

.btn-continuar,
.btn-enviar {
  background: var(--roxo-primario);
  color: var(--branco);
}

.btn-continuar:hover,
.btn-enviar:hover {
  background: var(--roxo-medio);
}

/* RESPONSIVIDADE */

@media (max-width: 700px) {
  #parceria-view {
    padding: 24px 5%;
  }

  .cabecalho {
    flex-direction: column;
  }

  .logo {
    width: 150px;
  }

  .formulario {
    padding: 20px;
  }

  .etapa small {
    display: none;
  }

  .acoes-formulario {
    flex-wrap: wrap;
  }

  .acoes-formulario button {
    flex: 1;
    min-width: 130px;
  }
}

@media (max-width: 480px) {
  .acoes-formulario {
    flex-direction: column-reverse;
  }

  .acoes-formulario button {
    width: 100%;
  }

  .cabecalho h1 {
    font-size: 23px;
  }
}
</style>
